import { CodePanel, type Chunk } from "@/components/guide/code-panel";
import {
  BovwExplainer,
  BowExplainer,
  ConcatExplainer,
  DescriptorExplainer,
  DiffExplainer,
  GraphExplainer,
  KmeansExplainer,
  ProblemExplainer,
  RetrievalExplainer,
  SiftExplainer,
  VladExplainer,
} from "@/components/guide/explainers";
import type { Chapter } from "@/components/guide/tutorial";
import { Screenshot } from "@/components/screenshot";

/**
 * Chapter 4: place recognition, as the kit's baseline does it. One idea per step, a figure
 * per idea, computed on real data: the problem, why pixels fail, keypoints, descriptors, bag
 * of words on text, visual words, bag of visual words, VLAD in two steps, retrieval, the graph
 * in three steps, the code, and the step that makes the baseline drive.
 */

const mono = (text: string) => <code className="font-mono text-xs">{text}</code>;

const RUN: Chunk[] = [{ from: 0, code: "uv run source/baseline_agent.py" }];

const VLAD_CODE: Chunk[] = [
  {
    from: 0,
    code: "# source/vlad.py, abridged\n\ndef describe(self, image):\n    # SIFT keypoints and 128-d descriptors\n    _, des = self.sift.detectAndCompute(image, None)\n    # RootSIFT: L1-normalise, then square root\n    des = des / np.sum(des, axis=1, keepdims=True)\n    return np.sqrt(des)\n\ndef fit(self, frames):\n    # the vocabulary: k-means over every descriptor of every frame\n    self.codebook = KMeans(n_clusters=128).fit(np.vstack(descriptors))\n    return np.array([self._vlad(d) for d in descriptors])\n\ndef _vlad(self, des):\n    labels = self.codebook.predict(des)\n    for k in range(self.n_clusters):\n        # the residuals of word k, summed, L2-normalised\n        vlad[k] = np.sum(des[labels == k] - centers[k], axis=0)\n        vlad[k] /= np.linalg.norm(vlad[k])\n    vlad = vlad.ravel()\n    vlad = np.sign(vlad) * np.sqrt(np.abs(vlad))  # power normalisation\n    return vlad / np.linalg.norm(vlad)",
  },
];

const AGENT_CODE: Chunk[] = [
  {
    from: 0,
    code: "# source/baseline_agent.py, abridged\n\ndef setup(self, info):\n    # the node most like the goal's front view\n    self.goal = int(np.argmax(self.database @ self.extractor.extract(info.targets[0])))\n\ndef act(self, obs):\n    # the node most like this frame, and the path from it\n    similarity = self.database @ self.extractor.extract(obs.image)\n    self.current = int(np.argmax(similarity))\n    self.path = nx.shortest_path(self.graph, self.current, self.goal, weight=\"weight\")",
  },
  { from: 0, until: 1, code: "    return super().act(obs)  # the keyboard still decides the move" },
  {
    from: 1,
    code: "    # yours: the move recorded on the path's first edge\n    if len(self.path) < 2:\n        return Action.CHECKIN\n    return Action[self._edge_action(self.path[0], self.path[1])]",
  },
];

export const baselineChapter: Chapter = {
  id: "baseline",
  label: "Baseline",
  title: "Place recognition: the baseline",
  intro: "The agent's first question is: where am I? The baseline answers it by recognising the place in the exploration frames, then plans a route over them. Run it, then take it apart one idea at a time.",
  steps: [
    {
      title: "Run it",
      body: (
        <>
          Same keys as the keyboard agent, plus hints. The status column names the exploration frame
          the baseline thinks you are at, the goal frame, the hops between them and the next move.
          The strip under the camera shows those frames. The first run builds the index in about a
          minute and caches it in {mono("cache/<challenge>/")}.
        </>
      ),
    },
    {
      title: "The problem",
      body: "You have one frame from now and hundreds from earlier drives. Find the old frame taken from where you are. Get that right and you know where you are, as a place in the exploration data. That is enough to plan from.",
    },
    {
      title: "Why not compare pixels?",
      body: "A small move changes almost every pixel. Two frames of the same corridor look very different pixel by pixel. So we need a description of an image that stays similar when the camera moves a little.",
    },
    {
      title: "Keypoints",
      body: "SIFT (Lowe, 2004) finds points that stand out: corners and blobs that can be found again from another distance or angle. A frame has a few hundred. Everything that follows works on these points, not on the whole image.",
    },
    {
      title: "Descriptors",
      body: "Around each keypoint SIFT records which way the brightness changes, as 128 numbers. Two views of the same corner give nearly the same 128 numbers even when the pixels differ. The kit uses RootSIFT (Arandjelović and Zisserman, 2012): take the square root of each number, which makes comparisons fairer.",
    },
    {
      title: "Bag of words, on text",
      body: "Here is the trick from text search. Turn a document into a count of its words and forget the order. Two documents with similar counts are about similar things. It is crude, and it works.",
    },
    {
      title: "Visual words",
      body: "Images have no words, so make some. Run k-means over all descriptors from all exploration frames. Each of the k = 128 centres is a visual word, a typical local pattern in this maze. Any descriptor is named by its nearest centre.",
    },
    {
      title: "Bag of visual words",
      body: "Name every keypoint by its word and count. The frame is now a histogram of 128 counts, exactly like the sentences. Frames of the same place have similar histograms. One thing is lost: where inside each word the descriptors were.",
    },
    {
      title: "VLAD: keep the residuals",
      body: (
        <>
          VLAD (Jégou et al., 2010) keeps that. For each word, add up the arrows from the centre to
          its descriptors: v<sub>k</sub> = Σ (x − c<sub>k</sub>). A count says how many; the sum also
          says where they were.
        </>
      ),
    },
    {
      title: "VLAD: one vector per frame",
      body: "Normalise each block, lay the 128 blocks end to end, take the signed square root of every number, normalise once more. Each frame is now one unit vector of 16,384 numbers. The similarity of two frames is their dot product.",
    },
    {
      title: "Retrieval",
      body: "Stack the exploration frames' vectors into a matrix. One query is a matrix-vector product and an argmax. This is the real result for one frame of the hero maze. The top match is not always right, so look at the gap to the runners-up. The goal photos are queries too.",
    },
    {
      title: "A graph of places",
      body: "Every exploration frame is a node. Consecutive frames of one drive are joined, and the edge remembers the move recorded between them. This is a map with no coordinates in it.",
    },
    {
      title: "Shortcuts",
      body: "Frames from different drives that look alike are probably the same place. The 30 most similar far-apart pairs get a shortcut edge, weighted by how alike they look. Now the drives join up into one map.",
    },
    {
      title: "The shortest path",
      body: "Match the current frame to a node. Match the goal photo to a node. Run Dijkstra between them. The move recorded on the first edge of the path is what to do now.",
    },
    {
      title: "Read vlad.py",
      body: (
        <>
          The whole descriptor pipeline is about a hundred lines. {mono("describe")} is SIFT and
          RootSIFT, {mono("fit")} builds the vocabulary and the database, {mono("_vlad")} is the
          aggregation from the last four steps. Two knobs: {mono("--n-clusters")} and{" "}
          {mono("--subsample")}.
        </>
      ),
    },
    {
      title: "Read baseline_agent.py",
      body: (
        <>
          {mono("setup")} finds the goal node once. {mono("act")} localises and replans on every
          step, then hands the move to the keyboard. {mono("--top-k")} sets how many shortcut edges
          the graph gets.
        </>
      ),
    },
    {
      title: "Make it drive",
      body: "The baseline hands the move to the keyboard. Return it from act() instead and you have your first autonomous agent. It will get lost: a wrong match sends it the wrong way, a shortcut joins two places that only look alike, and it never knows how far along an edge it is. Each of those is a place to start improving.",
    },
  ],
  preview: (step) =>
    [
      <div key="run" className="space-y-3">
        <CodePanel chunks={RUN} step={0} language="bash" title="Terminal" />
        <Screenshot
          src="/getting-started/baseline.webp"
          alt="Screenshot: the baseline running, with the best-matching exploration frame, the goal frame and the next nodes under the camera"
          className="w-full"
        />
      </div>,
      <ProblemExplainer key="problem" />,
      <DiffExplainer key="diff" />,
      <SiftExplainer key="sift" />,
      <DescriptorExplainer key="descriptor" />,
      <BowExplainer key="bow" />,
      <KmeansExplainer key="kmeans" />,
      <BovwExplainer key="bovw" />,
      <VladExplainer key="vlad" />,
      <ConcatExplainer key="concat" />,
      <RetrievalExplainer key="retrieval" />,
      <GraphExplainer key="g1" stage={1} />,
      <GraphExplainer key="g2" stage={2} />,
      <GraphExplainer key="g3" stage={3} />,
      <CodePanel key="vlad-code" chunks={VLAD_CODE} step={0} language="python" title="source/vlad.py" />,
      <CodePanel key="agent-code" chunks={AGENT_CODE} step={0} language="python" title="source/baseline_agent.py" />,
      <CodePanel key="drive" chunks={AGENT_CODE} step={1} language="python" title="source/baseline_agent.py" />,
    ][step],
};
